import { useEffect, useState, useRef, useContext } from "react";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import { useNavigate, useOutletContext } from "react-router";
import { useSelector } from "react-redux";
import FormCard from "../../common/forms/FormCard";
import FormField from "../../common/forms/FormField";
import SimpleButtonContentHeader from "../../common/mainApp/mainWindowHeaders/SimpleButtonContentHeader";
import ListComponent from "../../common/lists/ListComponent";
import { defaultStatusColors } from "../../../utils/CardUtils";
import FormFieldTagsDropdown from "../../common/forms/FormFieldTagsDropdown";
import AppContext from "../../../AppContext";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import * as Library from "../../../utils/Library";
import { formatPhoneNumber } from "../../../utils/Formatter";

// #region SCOPED CSS
const StyledPage = styled.div`
    .input-remove-default {
        border: none;
        outline: none;
        background: none;
    }

    .selects {
        background: var(--backgrounds-and-lines-n-600, #10141b);
    }

    .selects.mandatory {
        border: 1px solid transparent;
        border-left: 5px solid var(--text-icons-orange-500);
        border-radius: 4px;
        outline: none;
    }

    .selects.mandatory > div {
        border: none;
        cursor: pointer;
    }

    .selects.mandatory.error {
        border: 1px solid var(--text-icons-red-500);
        border-left: 5px solid var(--text-icons-red-500);
    }

    .main-window-header {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        display: flex;
        flex-direction: row;
        row-gap: 0px;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        height: 68px;
        position: relative;
    }
    .frame-40 {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        position: relative;
    }
    .btn-large-solid {
        background: var(--buttons-all-platforms-gray-300, #1e2631);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        row-gap: 0px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 120px;
        height: 35px;
        position: relative;
    }
    .button {
        color: var(--buttons-all-platforms-gray-200, #4f5b6d);
        text-align: left;
        font-family: var(--button-text-small-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--button-text-small-font-size, 10px);
        letter-spacing: var(--button-text-small-letter-spacing, 0.0125em);
        font-weight: var(--button-text-small-font-weight, 400);
        text-transform: uppercase;
        position: relative;
    }
`;
// #endregion SCOPED CSS

export default function UserInvitations() {
    // #region Page Title
    document.title = `${APP_TITLE} - Users Invitations`;
    // #endregion Page Title

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setHasUnsavedChanges, userData } = useContext(AppContext);
    const navigations = useSelector(state => state.navigationAccesses);
    const [isLoading, setIsLoading] = useState(false);
    const [saveBtnDisabled, setSaveBtnDisabled] = useState(false);
    const [imageData, setImageData] = useState(null);
    const navigate = useNavigate();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    const [openInviteDialog, setOpenInviteDialog] = useState(false);
    //------
    const formUserInviteID = useRef();
    const formUserInviteRefs = [];
    const formData = formUserInviteID?.current?.getAllFieldValues();
    const [allStores, setAllStores] = useState([]);
    const [inviteFormData, setInviteFormData] = useState({
        toAccount: "",
        firstName: "",
        lastName: "",
        role: "",
        accessLevel: "",
        email: "",
        phoneNumber: ""
    });
    const [allCompanies] = useState([
        {
            name: localStorage.getItem("currentCompanyName"),
            value: localStorage.getItem("currentCompanyID")
        }
    ]);

    // const labelbrandsIDs = formData?.toLabelBrand?.map(i => i.value);
    // const accessLevel =
    //     formData?.accessLevel?.name?.split(" ")[0] === "Account"
    //         ? "organization"
    //         : formData?.accessLevel?.name?.split(" ")[0]?.toLowerCase();

    // console.log(accessLevel, labelbrandsIDs);

    const userRole = userData?.role;

    // Dynamically set accessLevelDataOptions based on userRole
    let accessLevelDataOptions = [
        { id: "account", name: "Account level", value: "Account level" },
        { id: "company", name: "Company level", value: "Company level" },
        { id: "brand", name: "Retail Brand level", value: "Retail Brand level" },
        { id: "store", name: "Store level", value: "Store level" }
    ];

    // Use a locked map for accessLevelDataOptions just like for roles
    const lockedAccessLevelMap = {
        "Company Account Admin": ["account"],
        "Company Account Manager": ["account"],
        "Company Account User": ["account"],
        "Retail Brand Account Admin": ["account", "company"],
        "Retail Brand Account Manager": ["account", "company"],
        "Retail Brand Account User": ["account", "company", "brand"]
    };
    const lockedAccessLevels = lockedAccessLevelMap[userRole] || [];
    if (lockedAccessLevels.length) {
        accessLevelDataOptions = accessLevelDataOptions.map(option =>
            lockedAccessLevels.includes(option.id) ? { ...option, locked: true } : option
        );
    }

    const roleOptions = [
        [
            { name: "Account Admin", value: "Account Admin" },
            { name: "Account Manager", value: "Account Manager" },
            { name: "Account User", value: "Account User" }
        ],
        [
            { name: "Company Account Admin", value: "Company Account Admin" },
            { name: "Company Account Manager", value: "Company Account Manager" },
            { name: "Company Account User", value: "Company Account User" }
        ],
        [
            { name: "Retail Brand Account Admin", value: "Retail Brand Account Admin" },
            { name: "Retail Brand Account Manager", value: "Retail Brand Account Manager" },
            { name: "Retail Brand Account User", value: "Retail Brand Account User" }
        ],
        [
            { name: "Store Manager", value: "Store Manager" },
            { name: "Store User", value: "Store User" }
        ]
    ];

    const roleOptionIndex =
        inviteFormData.accessLevel === "Account level"
            ? 0
            : inviteFormData.accessLevel === "Company level"
              ? 1
              : inviteFormData.accessLevel === "Retail Brand level"
                ? 2
                : 3;

    const listColumns = [
        {
            name: "First name",
            selector: row => row?.contactFirstName ?? "--"
        },
        {
            name: "Last name",
            selector: row => row?.contactLastName ?? "--"
        },
        {
            name: "Role",
            selector: row => row?.userRole ?? "--"
        },
        {
            name: "Stage Level",
            selector: row => (row?.userAccessLevel === "Organization level" ? "Account level" : row?.userAccessLevel)
        },
        {
            name: "Phone number",
            selector: row => row?.contactMobileNumber ?? "--"
        },
        {
            name: "Email",
            selector: row => row?.contactEmail ?? "--"
        }
    ];

    // #endregion VARS

    // #region EFFECTS
    useEffect(() => {
        const saveIsEnabled = saveBtnDisabled && !isLoading;

        setupHeaders(props, saveIsEnabled, openInviteDialogBox, navigations);
    }, [props?.layoutType, saveBtnDisabled, isLoading]);

    useEffect(() => {
        getAllStores().then(setAllStores);
    }, []);

    // #endregion EFFECTS

    // #region METHODS
    const openInviteDialogBox = () => {
        setOpenInviteDialog(true);
    };

    const onInviteBtnClicked = () => {
        setIsLoading(true);

        // Debug: Log formData to check for missing/empty fields

        const phoneNum = formData.phoneNumber?.split(" ")[1];
        const phoneNumCountryCode = formData.phoneNumber?.split(" ")[0];
        // const labelbrandsIDs = formData.toLabelBrand?.map(i => i.value);
        // const accessLevel =
        //     formData.accessLevel?.name?.split(" ")[0] === "Account"
        //         ? "organization"
        //         : formData.accessLevel?.name?.split(" ")[0]?.toLowerCase();
        const inviteData = {
            // toAccount: formData.toAccount,
            profilePhotoData: imageData,
            firstName: formData.firstName,
            lastName: formData.lastName,
            userRole: formData.role?.name || "", // fallback to empty string if undefined
            userAccessLevel:
                formData.accessLevel?.value === "Account level"
                    ? "Organization level"
                    : formData.accessLevel?.value || "", // fallback to empty string if undefined
            email: formData.email,
            phoneNumber: phoneNum,
            phoneNumberCountryCode: phoneNumCountryCode,
            // [accessLevel + "ID"]:
            //     accessLevel === "organization" ? localStorage.getItem("currentOrganizationID") : labelbrandsIDs,

            organizationID: localStorage.getItem("currentOrganizationID"),
            userRestrictions: {
                companyIDs:
                    formData.accessLevel?.value === "Company level"
                        ? formData.toCompanies?.map(i => i.value)
                        : undefined,
                retailBrandIDs:
                    formData.accessLevel?.value === "Retail Brand level"
                        ? formData.toLabelBrand?.map(i => i.value)
                        : undefined,
                storeIDs:
                    formData.accessLevel?.value === "Store level" ? formData.toStore?.map(i => i.value) : undefined
            }
        };
        setHasUnsavedChanges(false);

        Library.makeCommonPostRequest("Invites", "inviteUser", inviteData, false, setIsLoading).then(res => {
            setOpenInviteDialog(false);
            if (res.data.status === 200) {
                Library.showToastMessage({
                    type: "success",
                    title: "Invitation Sent!",
                    message: `${formData.firstName} will get an invitation by email with the guidelines to join this account.`
                });
                navigate("/users/invitations");
            } else {
                setHasUnsavedChanges(true);
            }
        });
    };

    // Filter the role options based on the user's role
    const getFilteredRoleOptions = () => {
        let options = roleOptions?.[roleOptionIndex] || [];
        const lockedRolesMap = {
            "Account Admin": ["Account Admin"],
            "Account Manager": ["Account Admin", "Account Manager"],
            "Account User": ["Account Admin", "Account Manager", "Account User"],
            "Company Account Admin": ["Company Account Admin"],
            "Company Account Manager": ["Company Account Admin", "Company Account Manager"],
            "Company Account User": ["Company Account Admin", "Company Account Manager", "Company Account User"],
            "Retail Brand Account Admin": ["Retail Brand Account Admin"],
            "Retail Brand Account Manager": ["Retail Brand Account Admin", "Retail Brand Account Manager"],
            "Retail Brand Account User": [
                "Retail Brand Account Admin",
                "Retail Brand Account Manager",
                "Retail Brand Account User"
            ],
            "Store Manager": ["Store Manager"],
            "Store User": ["Store Manager", "Store User"]
        };
        const locked = lockedRolesMap[userRole] || [];
        if (locked.length) {
            options = options.map(option => (locked.includes(option.value) ? { ...option, locked: true } : option));
        }
        return options;
    };

    const roleOptionsFiltered = getFilteredRoleOptions();

    const checkFormIsValid = () => {
        const allCompanyIdFieldsAreValid = formUserInviteID.current.allFieldsAreValid(false);
        const allFieldsAreValid = allCompanyIdFieldsAreValid;

        setSaveBtnDisabled(allFieldsAreValid);
    };

    const handleAccessLevelChange = value => {
        if (value?.locked) {
            value.value = "";
        }
        const updatedInviteFormData = { ...inviteFormData, accessLevel: value?.value };

        if (value?.value === "Account level") {
            updatedInviteFormData.toAccount = localStorage.getItem("currentOrganizationName") || "";
            updatedInviteFormData.toCompanies = undefined; // Clear other fields
            updatedInviteFormData.toLabelBrand = undefined;
            updatedInviteFormData.toStore = undefined;
        } else if (value?.value === "Company level") {
            updatedInviteFormData.toAccount = "";
            updatedInviteFormData.toCompanies = allCompanies.length === 1 ? [allCompanies[0]?.name] : [];
            updatedInviteFormData.toLabelBrand = undefined;
            updatedInviteFormData.toStore = undefined;
        } else if (value?.value === "Retail Brand level") {
            updatedInviteFormData.toAccount = "";
            updatedInviteFormData.toCompanies = undefined;
            updatedInviteFormData.toLabelBrand = allBrands.length === 1 ? [allBrands[0]?.name] : [];
            updatedInviteFormData.toStore = undefined;
        } else if (value?.value === "Store level") {
            updatedInviteFormData.toAccount = "";
            updatedInviteFormData.toCompanies = undefined;
            updatedInviteFormData.toLabelBrand = undefined;
            updatedInviteFormData.toStore = allStores.length === 1 ? [allStores[0]?.name] : [];
        }

        setInviteFormData(updatedInviteFormData);
    };

    const getAllToBrands = () => {
        const toBrands = [];
        const labelBrands = JSON.parse(localStorage.getItem("retailBrands"));
        labelBrands?.forEach(i => {
            toBrands.push({ value: i?._id, name: i?.name });
        });
        return toBrands;
    };
    const [allBrands] = useState(getAllToBrands());

    const getAllStores = async () => {
        let allStores = [];
        const res = await Library.makeCommonPostRequest(
            "Global",
            "getFilters",
            {
                companyID: localStorage.getItem("currentCompanyID"),
                filters: ["Stores"]
            },
            false
        );
        const { data, status } = res?.data;
        if (status === 200) {
            const { stores } = data || {};
            stores.forEach(store => {
                allStores.push({ value: store?._id, name: store?.name });
            });
        } else {
            allStores = [];
        }
        return allStores;
    };

    // const getAllToCompanies = () => {
    //     const toCompanies = [];
    //     const companies = JSON.parse(localStorage.getItem("companies"));
    //     companies?.forEach(i => {
    //         toCompanies.push({ value: i?._id, name: i?.name });
    //     });
    //     return toCompanies;
    // };

    const handleRoleChange = value => {
        if (value?.locked) {
            value.value = "";
        }
        setInviteFormData({ ...inviteFormData, role: value?.value }); // Update role in state
        checkFormIsValid(); // Revalidate the form
    };

    // #endregion METHODS
    // #region FORM FIELDS
    const formUserInviteFields = [
        <FormField
            type="dropdown"
            dropdownData={accessLevelDataOptions}
            title="Access level"
            assistiveText=" "
            isDropdownSearchable={false}
            //tooltip="Select access level. This field is not mandatory."
            tooltip="Choose the user’s access scope for this account. This sets default permissions."
            tooltipMaxWidth="166px"
            requiredType={"mandatory"}
            varName="accessLevel"
            ref={el => (formUserInviteRefs.accessLevel = el)}
            value={inviteFormData.accessLevel} // Bind the value to the state
            onDropdownChange={handleAccessLevelChange}
            placeholder="Select access level"
        />,
        // --- Updated conditional rendering for "To" field ---
        inviteFormData.accessLevel === "Account level" || inviteFormData.accessLevel === "" ? (
            <FormField
                type="text"
                title="To Account"
                className="input-remove-default"
                //tooltip="This field is locked with the account name."
                tooltip="Pick the account this invite is for."
                tooltipMaxWidth="180px"
                requiredType="mandatoryLocked"
                assistiveText=" "
                varName="toAccount"
                ref={el => (formUserInviteRefs.toAccount = el)}
                value={inviteFormData.toAccount}
            />
        ) : inviteFormData.accessLevel === "Company level" ? (
            <FormFieldTagsDropdown
                type="text"
                autoCompleteStaticData={allCompanies}
                title="To Companies"
                //tooltip="Enter the company you want to send the invite to. Ensure the company name is accurate."
                tooltip="Pick the companies this invite is for."
                tooltipMaxWidth="125px"
                allowNewTags={false}
                varName="toCompanies"
                ref={el => (formUserInviteRefs.toCompanies = el)}
                requiredType={allCompanies.length === 1 ? "mandatoryLocked" : "mandatory"}
                value={inviteFormData.toCompanies}
                disableTagRemove={allCompanies.length === 1 ? true : false}
                useSelectAll={true}
                useDoneButton={true}
                selectAllText="Select All"
            />
        ) : inviteFormData.accessLevel === "Retail Brand level" ? (
            <FormFieldTagsDropdown
                type="text"
                autoCompleteStaticData={allBrands}
                title="To Retail Brands"
                //tooltip="Enter the brand you want to send the invite from. This is your brand. If you only have one brand enrolled, the field will be locked."
                tooltip="Pick the label brands this invite is for."
                tooltipMaxWidth="125px"
                requiredType={allBrands.length === 1 ? "mandatoryLocked" : "mandatory"}
                allowNewTags={false}
                varName="toLabelBrand"
                ref={el => (formUserInviteRefs.toLabelBrand = el)}
                value={inviteFormData.toLabelBrand}
                disableTagRemove={allBrands.length === 1 ? true : false}
                useSelectAll={true}
                useDoneButton={true}
                selectAllText="Select All"
            />
        ) : inviteFormData.accessLevel === "Store level" ? (
            <FormFieldTagsDropdown
                type="text"
                autoCompleteStaticData={allStores} // TODO: Replace with actual stores data if available
                title="To Store"
                //tooltip="Enter the store you want to send the invite to."
                tooltip="Pick the stores this invite is for."
                tooltipMaxWidth="125px"
                requiredType={allStores.length === 1 ? "mandatoryLocked" : "mandatory"}
                allowNewTags={false}
                varName="toStore"
                ref={el => (formUserInviteRefs.toStore = el)}
                value={inviteFormData.toStore}
                disableTagRemove={allStores.length === 1 ? true : false}
                useSelectAll={true}
                useDoneButton={true}
                selectAllText="Select All"
            />
        ) : null,
        <FormField
            type="text"
            title="First name"
            //tooltip="Enter the first name of the user to be invited. This field is not mandatory."
            tooltip="User’s given name as it will appear in the platform."
            tooltipMaxWidth="224px"
            requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            assistiveText=" "
            varName="firstName"
            ref={el => (formUserInviteRefs.firstName = el)}
        />,
        <FormField
            type="text"
            title="Last name"
            //tooltip="Enter the last name of the user to be invited. This field is not mandatory."
            tooltip="User’s family name as it will appear in the platform."
            tooltipMaxWidth="224px"
            //requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            requiredType="simple"
            assistiveText=" "
            varName="lastName"
            ref={el => (formUserInviteRefs.lastName = el)}
        />,
        <FormField
            type="dropdown"
            dropdownData={roleOptionsFiltered}
            title="Role"
            assistiveText=" "
            isDropdownSearchable={false}
            //tooltip="Select role. This field is not mandatory."
            tooltip="The permission role for this user (controls what the user can do)."
            tooltipMaxWidth="166px"
            requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            varName="role"
            ref={el => (formUserInviteRefs.role = el)}
            value={inviteFormData?.role} // Ensure the value is bound correctly
            onDropdownChange={handleRoleChange}
        />,
        <FormField
            type="phoneNumber"
            title="Phone number"
            tooltip="Contact number with country code; used for notifications when enabled."
            requiredType="simple"
            varName="phoneNumber"
            ref={el => (formUserInviteRefs.phoneNumber = el)}
            phoneNumberCountryCode={inviteFormData?.phoneNumberCountryCode}
            onCountryCodeChange={e => {
                setInviteFormData({
                    ...inviteFormData,
                    phoneNumberCountryCode: e.value
                });
            }}
            onTextChange={text => {
                setInviteFormData({
                    ...inviteFormData,
                    phoneNumber: text
                });
            }}
            value={formatPhoneNumber(inviteFormData?.phoneNumberCountryCode, inviteFormData?.phoneNumber)}
        />,
        <FormField
            type="email"
            title="Email"
            //tooltip="Enter the email address of the user to be invited. This field is not mandatory."
            tooltip="Invite will be sent here; must be unique and valid."
            tooltipMaxWidth="224px"
            requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            assistiveText=" "
            varName="email"
            ref={el => (formUserInviteRefs.email = el)}
        />
    ];
    // #endregion FORM FIELDS

    return (
        <StyledPage>
            <FullScreenOverlay show={openInviteDialog}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    loadingHeading="SAVING..."
                    title="Ready to invite?"
                    message={`Ready to invite ${formData?.firstName} at ${formData?.email}?`}
                    loading={isLoading}
                    confirmState="primary"
                    onClickYes={() => {
                        onInviteBtnClicked();
                    }}
                    onClickNo={() => {
                        setOpenInviteDialog(false);
                    }}
                />
            </FullScreenOverlay>
            <FormCard
                title="INVITE USER"
                ref={formUserInviteID}
                numOfFieldsPerLine={4}
                showDefaultRightLegends={true}
                formFields={formUserInviteFields}
                formFieldRefs={formUserInviteRefs}
                onFormChanged={checkFormIsValid}
                showUnsavedChangesPopup={true}
                //rightContent={<SmallFormFieldUpload imageData={imageData} setImageData={setImageData} />}
            />
            <div className="flex flex-dir-column gap-16 w-100">
                <ListComponent
                    title="INVITED USERS"
                    title2="(LAST 10 INVITED)"
                    columns={listColumns}
                    api={{
                        useCommonPostRequest: true,
                        commonContext: "Invites",
                        endpoint: "getInvitedUsersData",
                        formData: {
                            organizationID: localStorage.getItem("currentOrganizationID"),
                            limit: 10
                        }
                    }}
                    pageLimit={10}
                    currentPage={1}
                    queryKey={"invitedUsers"}
                    actions={{
                        includeActions: true,
                        extraActions: [
                            {
                                title: "Resend",
                                isDisabled: row => {
                                    return row?.status !== "Invited" && row?.status !== "Invited-Resent";
                                }
                            }
                        ],
                        editIsVisible: false,
                        suspendIsVisible: false,
                        removeIsDisabled: row => {
                            return ["Accepted", "Declined", "Voided"].includes(row?.status);
                        }
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "status",
                        statusColors: defaultStatusColors
                    }}
                />
            </div>
        </StyledPage>
    );
}

// #region SMALL COMPONENTS AND METHODS
function setupHeaders(props, saveIsEnabled, onBtnClicked, navigations) {
    //const createMode = navigations?.navigationAccesses?.retailers?.createModeOnly;

    props.setControlBarLeftBtns([]);
    props.setControlBarCenterBtns([
        { to: "/users", active: false, disabled: false, title: "users" },
        { to: "/users/invitations", active: true, disabled: false, title: "INVITES" }
    ]);
    props.setControlBarRightBtns([
        { to: "users/invitations", active: false, disable: false, title: "View", icon: "eye" },
        {
            to: "/users/invite",
            active: true,
            disabled: false,
            title: "Invite",
            icon: "mail"
        },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SimpleButtonContentHeader btnTitle="INVITE" btnEnabled={saveIsEnabled} onClick={onBtnClicked} />
        )
    });
}
// #endregion
